import React, { useState, useEffect } from 'react';
import { Settings2, AlertTriangle, Percent, DollarSign, Scale } from 'lucide-react';
import { clientesService } from '../../../services/api';
import Modal from './Modal';

const FORMATOS = [
  { valor: 'consolidada_detalle', etiqueta: 'Una línea con el detalle por máquina' },
  { valor: 'consolidada', etiqueta: 'Una línea, sin detalle' },
  { valor: 'por_maquina', etiqueta: 'Una línea por máquina' }
];

const claseInput = 'w-full h-10 px-3 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none';

// Cobro mensual de un cliente en Punto V: si se le cobra, cuánto (tarifa
// general de la empresa) y a qué cliente del ERP se le emite la cotización.
// Las tarifas por defecto (5 % o US$ 50 por máquina) las define Punto V.
// La tarifa mixta cobra el % de las ventas de cada máquina, y si ese monto no
// llega al mínimo cobra la tarifa fija; el cálculo lo hace Punto V.
const DialogoConfigCobro = ({ cliente, cuenta, tarifasPorDefecto, onGuardar, onCancel }) => {
  const porDefecto = (tipo) => (tarifasPorDefecto?.[tipo] !== undefined ? String(tarifasPorDefecto[tipo]) : '');
  const [form, setForm] = useState({
    cobro_habilitado: cuenta ? cuenta.cobro_habilitado : true,
    tarifa_tipo: cuenta?.tarifa?.tipo || 'porcentaje',
    tarifa_valor: cuenta?.tarifa ? String(cuenta.tarifa.valor) : porDefecto('porcentaje'),
    tarifa_fijo: cuenta?.tarifa?.fijo !== undefined ? String(cuenta.tarifa.fijo) : porDefecto('fijo'),
    tarifa_minimo: cuenta?.tarifa?.minimo !== undefined ? String(cuenta.tarifa.minimo) : porDefecto('minimo'),
    erp_cliente_id: cuenta?.erp_cliente_id ? String(cuenta.erp_cliente_id) : '',
    formato_cotizacion: cuenta?.formato_cotizacion || 'consolidada_detalle',
    dia_minimo_inactivas: String(cuenta?.dia_minimo_inactivas ?? tarifasPorDefecto?.dia_minimo_inactivas ?? 15)
  });
  const [clientesErp, setClientesErp] = useState([]);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    clientesService.getAll({ activo: true })
      .then(res => setClientesErp((res.data || []).filter(c => c.activo !== false)))
      .catch(() => {});
  }, []);

  const cambiar = (campo, valor) => setForm(prev => ({ ...prev, [campo]: valor }));

  // tarifa_valor es el % en 'porcentaje' y 'mixta', y el monto en 'fijo'.
  const claveDefecto = (tipo) => (tipo === 'fijo' ? 'fijo' : 'porcentaje');

  // Al cambiar de tipo se propone la tarifa por defecto del nuevo tipo,
  // salvo que el valor actual sea uno escrito a mano.
  const cambiarTipo = (tipo) => setForm(prev => {
    const eraPorDefecto = prev.tarifa_valor === '' || prev.tarifa_valor === porDefecto(claveDefecto(prev.tarifa_tipo));
    return { ...prev, tarifa_tipo: tipo, tarifa_valor: eraPorDefecto ? porDefecto(claveDefecto(tipo)) : prev.tarifa_valor };
  });

  const guardar = async (e) => {
    e.preventDefault();
    setError('');
    setGuardando(true);
    try {
      await onGuardar({
        cobro_habilitado: form.cobro_habilitado,
        tarifa_tipo: form.tarifa_tipo,
        tarifa_valor: String(form.tarifa_valor).trim(),
        ...(form.tarifa_tipo === 'mixta' && {
          tarifa_fijo: String(form.tarifa_fijo).trim(),
          tarifa_minimo: String(form.tarifa_minimo).trim()
        }),
        erp_cliente_id: form.erp_cliente_id ? Number(form.erp_cliente_id) : null,
        formato_cotizacion: form.formato_cotizacion,
        dia_minimo_inactivas: Number(form.dia_minimo_inactivas)
      });
    } catch (err) {
      setError(err.message || 'No se pudo guardar');
      setGuardando(false);
    }
  };

  return (
    <Modal
      titulo={`Cobro de ${cliente.slug}`}
      subtitulo={`${cliente.duenio.nombre} · ${cliente.base_datos}`}
      icono={Settings2}
      onClose={onCancel}
      bloqueado={guardando}
      pie={(
        <>
          <button type="button" onClick={onCancel} disabled={guardando} className="px-4 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100">Cancelar</button>
          <button type="submit" form="form-config-cobro" disabled={guardando} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50">
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      )}
    >
      <form id="form-config-cobro" onSubmit={guardar} className="space-y-5">
        <label className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 cursor-pointer">
          <input
            type="checkbox"
            role="switch"
            checked={form.cobro_habilitado}
            onChange={(e) => cambiar('cobro_habilitado', e.target.checked)}
            className="mt-1"
          />
          <span>
            <span className="block text-sm font-medium text-gray-800">Cobrar mensualidad a este cliente</span>
            <span className="block text-xs text-gray-500">
              {form.cobro_habilitado
                ? 'El día 1 de cada mes Punto V calcula su mensualidad y se le envía la cotización.'
                : 'No se le genera cobro ni se le corta la recarga por falta de pago.'}
            </span>
          </span>
        </label>

        <fieldset>
          <legend className="block text-sm font-medium text-gray-700 mb-2">Tarifa</legend>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
            {[
              { valor: 'porcentaje', titulo: 'Porcentaje', texto: 'De lo que vende cada máquina en el mes', icono: Percent },
              { valor: 'fijo', titulo: 'Monto fijo', texto: 'Por cada máquina activa, en USD', icono: DollarSign },
              { valor: 'mixta', titulo: 'Porcentaje con mínimo', texto: 'Si el % de una máquina no llega al mínimo, se cobra la fija', icono: Scale }
            ].map(op => (
              <label key={op.valor} className={`flex items-start gap-2 p-3 rounded-xl border cursor-pointer ${form.tarifa_tipo === op.valor ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                <input type="radio" name="tarifa-tipo" checked={form.tarifa_tipo === op.valor} onChange={() => cambiarTipo(op.valor)} className="mt-0.5" />
                <span>
                  <span className="flex items-center gap-1.5 text-sm font-medium text-gray-800"><op.icono size={14} /> {op.titulo}</span>
                  <span className="text-xs text-gray-500">{op.texto}</span>
                </span>
              </label>
            ))}
          </div>
          <div className="flex items-center gap-2 max-w-xs">
            {form.tarifa_tipo === 'fijo' && <span className="text-sm text-gray-500">US$</span>}
            <input
              type="number"
              inputMode="decimal"
              min="0.01"
              max={form.tarifa_tipo === 'fijo' ? undefined : 100}
              step="0.01"
              value={form.tarifa_valor}
              onChange={(e) => cambiar('tarifa_valor', e.target.value)}
              required
              aria-label="Valor de la tarifa"
              placeholder={form.tarifa_tipo === 'fijo' ? '50.00' : '5'}
              className={`${claseInput} tabular-nums`}
            />
            <span className="text-sm text-gray-500 whitespace-nowrap">{form.tarifa_tipo === 'fijo' ? 'por máquina' : '% de ventas'}</span>
          </div>
          {form.tarifa_tipo === 'mixta' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <label className="block">
                <span className="block text-xs text-gray-600 mb-1">Si el % de una máquina es menor a</span>
                <span className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">US$</span>
                  <input
                    type="number" inputMode="decimal" min="0.01" step="0.01" required
                    value={form.tarifa_minimo}
                    onChange={(e) => cambiar('tarifa_minimo', e.target.value)}
                    placeholder="50.00"
                    className={`${claseInput} tabular-nums`}
                  />
                </span>
              </label>
              <label className="block">
                <span className="block text-xs text-gray-600 mb-1">se cobra la tarifa fija de</span>
                <span className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">US$</span>
                  <input
                    type="number" inputMode="decimal" min="0.01" step="0.01" required
                    value={form.tarifa_fijo}
                    onChange={(e) => cambiar('tarifa_fijo', e.target.value)}
                    placeholder="50.00"
                    className={`${claseInput} tabular-nums`}
                  />
                  <span className="text-sm text-gray-500 whitespace-nowrap">por máquina</span>
                </span>
              </label>
            </div>
          )}
          {form.tarifa_tipo === 'mixta' && form.tarifa_valor && form.tarifa_minimo && form.tarifa_fijo && (
            <p className="mt-2 text-xs text-gray-500">
              Ejemplo: una máquina que vende US$ {(Number(form.tarifa_minimo) * 100 / Number(form.tarifa_valor)).toLocaleString('es-VE', { maximumFractionDigits: 2 })} o más paga el {form.tarifa_valor} %; si vende menos, paga US$ {form.tarifa_fijo}.
            </p>
          )}
          {form.tarifa_tipo !== 'mixta' && porDefecto(form.tarifa_tipo) && (
            <p className="mt-1 text-xs text-gray-400">
              Tarifa por defecto: {form.tarifa_tipo === 'porcentaje' ? `${porDefecto('porcentaje')} % de las ventas` : `US$ ${porDefecto('fijo')} por máquina`}.
            </p>
          )}
          {cuenta?.tarifas_por_maquina > 0 && (
            <p className="mt-2 text-xs text-amber-700 flex items-start gap-1.5">
              <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
              Tiene {cuenta.tarifas_por_maquina} tarifa(s) propia(s) de máquina en Punto V: esas máquinas usan la suya, no esta.
            </p>
          )}
        </fieldset>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-1">Cliente del ERP</span>
            <select value={form.erp_cliente_id} onChange={(e) => cambiar('erp_cliente_id', e.target.value)} className={claseInput}>
              <option value="">Sin vincular</option>
              {clientesErp.map(c => (
                <option key={c.id} value={c.id}>{[c.nombre, c.apellido].filter(Boolean).join(' ')}{c.rif ? ` · ${c.rif}` : ''}</option>
              ))}
            </select>
            {!form.erp_cliente_id && form.cobro_habilitado && (
              <span className="block mt-1 text-xs text-amber-700">Sin cliente del ERP no se puede emitir la cotización de la mensualidad.</span>
            )}
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-1">Formato de la cotización</span>
            <select value={form.formato_cotizacion} onChange={(e) => cambiar('formato_cotizacion', e.target.value)} className={claseInput}>
              {FORMATOS.map(f => <option key={f.valor} value={f.valor}>{f.etiqueta}</option>)}
            </select>
          </label>
        </div>

        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Máquinas inactivadas en el mes</span>
          <span className="flex items-center gap-2 text-sm text-gray-600">
            Se cobran si trabajaron hasta el día
            <input
              type="number"
              min="1"
              max="31"
              step="1"
              required
              value={form.dia_minimo_inactivas}
              onChange={(e) => cambiar('dia_minimo_inactivas', e.target.value)}
              aria-label="Día mínimo para cobrar máquinas inactivas"
              className="w-20 h-9 px-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none tabular-nums"
            />
            o después
          </span>
          <span className="block mt-1 text-xs text-gray-500">
            Se toma la fecha de su última venta del mes. Si dejó de vender antes de ese día, ese mes no se le cobra. Por defecto: día 15.
          </span>
        </label>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
      </form>
    </Modal>
  );
};

export default DialogoConfigCobro;
